import { useCallback } from 'react';
import { addMessageFeedback, clearMessages, removeMessageFeedback, setConversation, setMessageFailure, setMessageSuccess,loadExistingMessageSuccess, setPendingMessage, setLoading, setTablePaginatedDataSet, setPdfDownloadLoading, setPdfDownloadError, addInsightToMessage } from 'features/chat-slice';
import { setActiveConversation, setNewConversation } from 'features/conversation-slice';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {  RootState } from '../redux/store';
import { useAiChatApiService } from 'api/providers/AppServiceProvider';
import { Conversation, FeedbackRequest, StreamingResponse, MainResultResponse, InsightResponse, InsightsCompleteResponse } from 'models/ai-chat/ai-chat-response';
import { TableRequest } from 'models/ai-chat/table-pagination';
import { FilterItem } from 'models/ai-chat/filter-request';

export const useChat = () => {
  const dispatch = useAppDispatch();
  const { 
    messages, 
    pendingMessage, 
    error, 
    currentConversationId,
    suggestions,
    suggestedQuestions,
    suggestedVisualizations, 
    loading,
    isDownloadingPDF,
    pdfDownloadError,
    pdfDownloadErrorMessageId
  } = useAppSelector((state: RootState) => state.chat);
  
  const aiChatApiService = useAiChatApiService();
  const { activeConversationId } = useAppSelector((state: RootState) => state.conversations);

  const sendChatMessage = useCallback(
    async (message: string) => {
      //console.log('🎯 sendChatMessage called with:', message);
      if (!message.trim()) return;
      
      // Check if this is an edit or regenerate message
      const editMatch = message.match(/^\[EDIT:([^\]]+)\]\s*(.+)$/);
      const regenerateMatch = message.match(/^\[REGENERATE:([^\]]+)\]\s*(.+)$/);
      const isEdit = !!editMatch;
      const isRegenerate = !!regenerateMatch;
      const actualMessage = isEdit ? editMatch![2] : isRegenerate ? regenerateMatch![2] : message;
      const editMessageId = isEdit ? editMatch![1] : isRegenerate ? regenerateMatch![1] : undefined;
      
      // console.log('📝 Message type:', isEdit ? 'EDIT' : isRegenerate ? 'REGENERATE' : 'NEW');
      // console.log('📝 Actual message:', actualMessage);
      // console.log('📝 Edit/Regenerate message ID:', editMessageId);
      
      await dispatch(setPendingMessage({ 
        prompt: actualMessage, 
        isEdit: isEdit, 
        editMessageId: editMessageId,
        isRegenerate: isRegenerate
      }));
      
      // // Use the explanation query API with streaming
      // console.log('🔍 Checking if sendExplanationQueryRequest exists:', typeof aiChatApiService.sendExplanationQueryRequest);
      // console.log('🔍 aiChatApiService methods:', Object.keys(aiChatApiService));
      
      if (typeof aiChatApiService.sendExplanationQueryRequest === 'function') {
        let accumulatedResponse = '';
        let conversationId = currentConversationId;
        let chunkCount = 0;
        const startTime = Date.now();
        const startTimeFormatted = new Date().toISOString();
        // console.log(`[${startTimeFormatted}] Starting explanation query streaming for: "${actualMessage}"`);
        
        // Set a timeout to clear pending state if streaming takes too long (5 minutes)
        const timeoutId = setTimeout(() => {
          //console.warn('Streaming timeout - clearing pending state');
          dispatch(setMessageFailure('Request timeout - please try again'));
        }, 5 * 60 * 1000); // 5 minutes timeout
        
        // Create conversation if needed (for new messages, not edits)
        if (!isEdit && !currentConversationId) {
          // We'll create the conversation when we get the first response with conversation_id
          // console.log('🆕 New conversation will be created when first response arrives');
        }
        
        aiChatApiService.sendExplanationQueryRequest({
          prompt: actualMessage,
          message_id: editMessageId,
          conversation_id: currentConversationId || undefined,
          is_edited: isEdit,
          is_regenerate: isRegenerate
        }, 
        // onChunk - handle streaming chunks
        (chunk: any) => {
          chunkCount++;
          const currentTime = Date.now();
          const timeSinceStart = currentTime - startTime;
          const timestamp = new Date().toISOString();
          const timeWithMs = timestamp.split('T')[1].replace('Z', ''); // Get HH:MM:SS.sss
          // console.log(`[${timeWithMs}] [+${timeSinceStart}ms] Chunk #${chunkCount}:`, chunk);
          
          // Handle different response types
          if (typeof chunk === 'object' && chunk.type) {
            handleStreamingResponse(chunk, conversationId, actualMessage);
          } else if (typeof chunk === 'string') {
            accumulatedResponse += chunk;
          }
        },
        // onError - handle errors
        (error: Error) => {
          console.error('Streaming error:', error);
          clearTimeout(timeoutId); // Clear timeout
          dispatch(setMessageFailure(error.message));
        },
        // onComplete - handle completion
        () => {
          const endTime = Date.now();
          const totalTime = endTime - startTime;
          const endTimeFormatted = new Date().toISOString();
          const timeWithMs = endTimeFormatted.split('T')[1].replace('Z', '');
          console.log(`[${timeWithMs}] [+${totalTime}ms] Streaming completed - Received ${chunkCount} chunks, Total response length: ${accumulatedResponse.length} characters`);
          
          clearTimeout(timeoutId); // Clear timeout
          
          // Clear pending state when streaming completes
          dispatch(setLoading(false));
          
          // Ensure pending message is cleared
          if (chunkCount === 0) {
            // If no chunks were received, clear pending state
            dispatch(setMessageFailure('No response received from server'));
          }
          
          // No need to create a final response - the main_result and insights already handle message creation
          // The streaming responses (main_result, insight, insights_complete) already create/update the appropriate messages
        }
        ).catch((error: any) => {
          console.log('Explanation query error:', error);
          clearTimeout(timeoutId); // Clear timeout
          // Don't dispatch setMessageFailure here as it's already handled in onError callback
          // This prevents duplicate error messages
        });
      } else {
        // Fallback to regular query if explanation query is not available
        console.warn('sendExplanationQueryRequest not available, falling back to sendQueryRequest');
        aiChatApiService.sendQueryRequest({
          prompt: actualMessage,
          conversation_id: currentConversationId || undefined,
        }).then(
          (response: any) => {
            if(!currentConversationId){
              let conv :Conversation = {
                conversation_id: response.conversation_id,
                title: actualMessage || 'New Conversation',
                updated_at: new Date().toISOString(),
                latest_message: {
                  content: actualMessage,
                  role: 'user',
                  created_at: new Date().toISOString(),
                  sql_query: '',
                  feedback: null
                },
                created_at: new Date().toISOString()
              }
              dispatch(setNewConversation(conv));
            }
            dispatch(setMessageSuccess(response));
          },
          (error:any) => {
            console.log(error);
            dispatch(setMessageFailure(error.message));
          }
        );
      }
    },
    [dispatch, currentConversationId]
  );


  const startNewConversation = useCallback(() => {
    dispatch(setConversation(null));
    dispatch(setActiveConversation(null));
    dispatch(clearMessages());
  }, [dispatch]);

  const loadConversation = useCallback(
    (conversationId: string) => {
      dispatch(setLoading(true));
      dispatch(setConversation(conversationId));
      dispatch(setActiveConversation(conversationId));
      aiChatApiService.getConversationById(conversationId).then(
        (response: any) => {
         dispatch(loadExistingMessageSuccess(response));
         dispatch(setLoading(false));
        },
        (error:any) => {
          console.log(error);
          dispatch(setLoading(false));
          dispatch(setMessageFailure(error.message));
        }
      );
    },
    [dispatch]
  );

  const sendSuggestion = useCallback(
    (suggestion: string) => {
      sendChatMessage(suggestion);
    },
    [sendChatMessage]
  );

  const feedbackMessageRequest = useCallback(
    async (feedback: FeedbackRequest) => {
      dispatch(addMessageFeedback(feedback));
      aiChatApiService.addMessageFeedback(feedback).then(
        (response: any) => {
          if(response!=null){
            dispatch(addMessageFeedback(feedback));
          }
        },
        (error) => {
          console.log(error);
          dispatch(removeMessageFeedback(feedback));
        }
      );
    },
    [dispatch]
  );


  const getTableDataRequest = useCallback(
    async (table: TableRequest) => {
         dispatch(setLoading(true));
      aiChatApiService.getTablePaginationData(table).then(
        (response: any) => {
          if(response!=null){
           dispatch(setTablePaginatedDataSet(response));
          }
          dispatch(setLoading(false));
        },
        (error) => {
          console.log(error);
          dispatch(setLoading(false));
        }
      );
    },
    [dispatch]
  );

  const formatUtcForServerLikeName = (): string =>{
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    '_' +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds())
  );
}

 const downloadTableAsPDF = useCallback(
  async (
    messageId: string,
    startDate?: string,
    toDate?: string,
    filterData?: FilterItem[]
  ) => {
    if (!messageId) {
      dispatch(setPdfDownloadError({ error: 'No message ID available for PDF download', messageId: messageId }));
      return;
    }

    dispatch(setPdfDownloadLoading(true));

    try {
      const pdfBlob = await aiChatApiService.downloadTableAsPDF(messageId, startDate, toDate, filterData);

      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;

      let filename = 'Account_Statement';

      if (startDate && toDate) {
        const cleanStart = startDate.replace(/-/g, '');
        const cleanEnd = toDate.replace(/-/g, '');
        filename += `_${cleanStart}_${cleanEnd}`;
      } else {
        filename += `_${formatUtcForServerLikeName()}`;
      }

      filename += '.pdf';

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      dispatch(setPdfDownloadLoading(false));
    } catch (error) {
      console.error('Error downloading PDF:', error);
      
      let userFriendlyMessage = 'Failed to generate PDF';
      
      if (error instanceof Error) {
        if (error.message.includes('500')) {
          userFriendlyMessage = 'Failed to generate PDF. Please try again later.';
        } else if (error.message.includes('404')) {
          userFriendlyMessage = 'PDF generation service not available.';
        } else if (error.message.includes('403')) {
          userFriendlyMessage = 'Access denied for PDF generation.';
        } else if (error.message.includes('timeout') || error.message.includes('time out')) {
          userFriendlyMessage = 'PDF generation is taking too long. Please try again.';
        } else if (error.message.includes('network') || error.message.includes('connection')) {
          userFriendlyMessage = 'Network error. Please check your connection and try again.';
        }
      }
      
      dispatch(setPdfDownloadError({ error: userFriendlyMessage, messageId: messageId }));
    }
  },
  [dispatch]
);

  const clearPDFDownloadError = useCallback(() => {
    dispatch(setPdfDownloadError({ error: null, messageId: null }));
  }, [dispatch]);

  // Handle different streaming response types
  const handleStreamingResponse = useCallback((response: StreamingResponse, conversationId: string | null, originalMessage?: string) => {
   // console.log('🔄 Handling streaming response:', response);
    
    switch (response.type) {
      case 'main_result':
        handleMainResultResponse(response as MainResultResponse, conversationId, originalMessage);
        break;
      case 'insight':
        handleInsightResponse(response as InsightResponse);
        break;
      case 'insights_complete':
        handleInsightsCompleteResponse(response as InsightsCompleteResponse);
        break;
      default:
        console.warn('Unknown response type:', response);
    }
  }, [dispatch]);

  const handleMainResultResponse = useCallback((response: MainResultResponse, conversationId: string | null, originalMessage?: string) => {
    console.log('📊 Handling main result response:', response);
    
    // Create conversation if it doesn't exist
    if (!conversationId) {
      // Use the original message as the conversation title
      const conversationTitle = originalMessage || response.text || 'New Conversation';
      
      const conv: Conversation = {
        conversation_id: response.conversation_id,
        title: conversationTitle.length > 100 ? conversationTitle.substring(0, 100) + '...' : conversationTitle,
        updated_at: new Date().toISOString(),
        latest_message: {
          content: originalMessage || response.text || 'New query',
          role: 'user',
          created_at: new Date().toISOString(),
          sql_query: response.sql_query,
          feedback: null
        },
        created_at: new Date().toISOString()
      };
      dispatch(setNewConversation(conv));
    }
    
    // Convert to QueryResponse format for compatibility
    const queryResponse = {
      text: response.text,
      visualization: response.visualization,
      data: response.data,
      conversation_id: response.conversation_id,
      sql_query: response.sql_query,
      suggested_visualizations: [],
      suggested_questions: [],
      message_id: response.message_id,
      metadata: response.metadata,
      type: response.type,
      parent_message_id:response.parent_message_id
    };
    
    dispatch(setMessageSuccess(queryResponse));
  }, [dispatch]);

  const handleInsightResponse = useCallback((response: InsightResponse) => {
    const insightValue = response.result?.llm_insights?.insights;
    const hasInsightPayload = typeof insightValue === 'string';
    const rawReportData = response.result?.report_data;
    const reportData = Array.isArray(rawReportData) ? rawReportData : [];
    const hasReportData = reportData.length > 0;

    if (hasInsightPayload || hasReportData) {
      dispatch(
        addInsightToMessage({
          messageId: response.message_id,
          insightType: response.insight_type,
          insight: hasInsightPayload ? insightValue! : '',
          report_data: reportData
        })
      );
    }
  }, [dispatch]);

  const handleInsightsCompleteResponse = useCallback((response: InsightsCompleteResponse) => {
   // console.log('✅ Insights completed:', response);
   // console.log(`Completed insights: ${response.completed_insights.join(', ')}`);
    //console.log(`Total time: ${response.total_time}`);
    
    // Stop loading when insights are complete
    dispatch(setLoading(false));
  }, [dispatch]);

  return {
    messages,
    pendingMessage,
    error,
    currentConversationId,
    activeConversationId,
    suggestions,
    suggestedVisualizations,
    suggestedQuestions,
    loading,
    isDownloadingPDF,
    pdfDownloadError,
    pdfDownloadErrorMessageId,
    sendChatMessage,
    startNewConversation,
    loadConversation,
    sendSuggestion,
    feedbackMessageRequest,
    getTableDataRequest,
    downloadTableAsPDF,
    clearPDFDownloadError
  };
};